import React, { useState, useEffect } from 'react';

const Staff = () => {
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Static data for models and paths
  const availableModels = ["Users", "Reports", "Settings", "Dashboard", "Posters", "Reels", "Audio", "Logos", "BusinessCards", "Stickers", "Banners", "SubscriptionPlans"];
  
  const availablePaths = {
    "Users": ["/users", "/users/create", "/users/edit", "/users/delete"],
    "Reports": ["/reports", "/reports/view", "/reports/export"],
    "Settings": ["/settings", "/settings/profile", "/settings/privacy"],
    "Dashboard": ["/dashboard", "/dashboard/stats"],
    "Posters": ["/posters", "/posters/create", "/posters/edit", "/posters/delete"],
    "Reels": ["/reels", "/reels/create", "/reels/edit", "/reels/delete"],
    "Audio": ["/audio", "/audio/create", "/audio/edit", "/audio/delete"],
    "Logos": ["/logos", "/logos/create", "/logos/edit", "/logos/delete"],
    "BusinessCards": ["/businesscards", "/businesscards/create", "/businesscards/edit", "/businesscards/delete"],
    "Stickers": ["/stickers", "/stickers/create", "/stickers/edit", "/stickers/delete"],
    "Banners": ["/banners", "/banners/create", "/banners/edit", "/banners/delete"],
    "SubscriptionPlans": ["/plans", "/plans/create", "/plans/edit", "/plans/delete"]
  };
  
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    isActive: true,
    permissions: [],
    profileImage: null
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await fetch('https://api.editezy.com/api/staff/getallstaff');
      const data = await response.json();
      
      if (data.success) {
        setStaffMembers(data.data);
      } else {
        setError('Failed to fetch staff data');
      }
    } catch (err) {
      setError('Error fetching staff: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch staff by ID
  const fetchStaffById = async (staffId) => {
    setDetailLoading(true);
    try {
      const response = await fetch(`https://api.editezy.com/api/staff/getstaff/${staffId}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedStaff(data.data);
        setShowDetailModal(true);
      } else {
        alert('Failed to fetch staff details');
      }
    } catch (err) {
      alert('Error fetching staff details: ' + err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  // Handle Edit button click
  const handleEditClick = (staff) => {
    setSelectedStaff(staff);
    setEditFormData({
      name: staff.name || '',
      email: staff.email || '',
      phone: staff.phone || '',
      isActive: staff.isActive !== undefined ? staff.isActive : true,
      permissions: staff.permissions || [],
      profileImage: null
    });
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  // Handle Edit form changes
  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle Permission changes in Edit form
  const handlePermissionChange = (moduleIndex, pathIndex, field, value) => {
    setEditFormData(prev => {
      const newPermissions = [...prev.permissions];
      if (field === 'module') {
        newPermissions[moduleIndex].module = value;
        // Reset paths when module changes
        newPermissions[moduleIndex].paths = [{ path: '', accessLevel: 'view' }];
      } else if (field === 'path') {
        newPermissions[moduleIndex].paths[pathIndex].path = value;
      } else if (field === 'accessLevel') {
        newPermissions[moduleIndex].paths[pathIndex].accessLevel = value;
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  // Get available paths for a specific module
  const getPathsForModule = (moduleName) => {
    if (!moduleName) return [];
    return availablePaths[moduleName] || [];
  };

  // Add new permission module
  const addPermissionModule = () => {
    setEditFormData(prev => ({
      ...prev,
      permissions: [
        ...prev.permissions,
        { module: '', paths: [{ path: '', accessLevel: 'view' }] }
      ]
    }));
  };

  // Add new path to permission
  const addPermissionPath = (moduleIndex) => {
    setEditFormData(prev => {
      const newPermissions = [...prev.permissions];
      newPermissions[moduleIndex].paths.push({ path: '', accessLevel: 'view' });
      return { ...prev, permissions: newPermissions };
    });
  };

  // Remove permission module
  const removePermissionModule = (moduleIndex) => {
    setEditFormData(prev => ({
      ...prev,
      permissions: prev.permissions.filter((_, idx) => idx !== moduleIndex)
    }));
  };

  // Remove permission path
  const removePermissionPath = (moduleIndex, pathIndex) => {
    setEditFormData(prev => {
      const newPermissions = [...prev.permissions];
      newPermissions[moduleIndex].paths = newPermissions[moduleIndex].paths.filter(
        (_, idx) => idx !== pathIndex
      );
      return { ...prev, permissions: newPermissions };
    });
  };

  // Handle file upload
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setEditFormData(prev => ({
        ...prev,
        profileImage: e.target.files[0]
      }));
    }
  };

  // Update Staff
  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    setEditLoading(true);

    try {
      const permissions = editFormData.permissions
        .map((permission) => ({
          ...permission,
          paths: (permission.paths || []).filter((path) => path.path),
        }))
        .filter((permission) => permission.module && permission.paths.length > 0);
      const formData = new FormData();
      formData.append('name', editFormData.name);
      formData.append('email', editFormData.email);
      formData.append('phone', editFormData.phone);
      formData.append('isActive', editFormData.isActive);
      formData.append('permissions', JSON.stringify(permissions));
      
      if (editFormData.profileImage) {
        formData.append('profileImage', editFormData.profileImage);
      }

      const response = await fetch(
        `https://api.editezy.com/api/staff/updatestaff/${selectedStaff._id}`,
        {
          method: 'PUT',
          body: formData
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Staff member updated successfully!');
        setShowEditModal(false);
        // Refresh the staff list
        fetchStaff();
      } else {
        alert('Failed to update staff: ' + (data.message || data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error updating staff: ' + err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Delete Staff
  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) {
      return;
    }

    setDeleteLoading(true);
    try {
      const response = await fetch(
        `https://api.editezy.com/api/staff/deletestaff/${staffId}`,
        {
          method: 'DELETE'
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Staff member deleted successfully!');
        setDeleteConfirm(null);
        // Refresh the staff list
        fetchStaff();
      } else {
        alert('Failed to delete staff: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      alert('Error deleting staff: ' + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const dataToExport = filteredStaff.length > 0 ? filteredStaff : staffMembers;
    
    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Define headers
    const headers = ['S.No', 'Name', 'Email', 'Phone', 'Status', 'Permissions'];
    
    // Create rows
    const rows = dataToExport.map((staff, index) => [
      index + 1,
      staff.name,
      staff.email,
      staff.phone,
      staff.isActive ? 'Active' : 'Inactive',
      staff.permissions.map(p => `${p.module} (${p.paths.length})`).join('; ')
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `staff_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export to Excel
  const exportToExcel = () => {
    const dataToExport = filteredStaff.length > 0 ? filteredStaff : staffMembers;
    
    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    let tableHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" 
            xmlns:x="urn:schemas-microsoft-com:office:excel" 
            xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Staff</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          table { border-collapse: collapse; width: 100%; }
          th { background-color: #4F81BD; color: white; font-weight: bold; padding: 8px; border: 1px solid #000; }
          td { padding: 8px; border: 1px solid #000; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Permissions</th>
              <th>Created At</th>
              <th>Last Login</th>
            </tr>
          </thead>
          <tbody>
    `;

    dataToExport.forEach((staff, index) => {
      const permissions = staff.permissions.map(p => 
        `${p.module} (${p.paths.map(path => `${path.path}:${path.accessLevel}`).join(', ')})`
      ).join('; ');
      
      tableHtml += `
        <tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(staff.name)}</td>
          <td>${escapeHtml(staff.email)}</td>
          <td>${escapeHtml(staff.phone)}</td>
          <td>${staff.isActive ? 'Active' : 'Inactive'}</td>
          <td>${escapeHtml(permissions)}</td>
          <td>${new Date(staff.createdAt).toLocaleString()}</td>
          <td>${staff.lastLogin ? new Date(staff.lastLogin).toLocaleString() : 'Never'}</td>
        </tr>
      `;
    });

    tableHtml += `
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob([tableHtml], { 
      type: 'application/vnd.ms-excel;charset=utf-8' 
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `staff_export_${new Date().toISOString().split('T')[0]}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return String(text).replace(/[&<>"']/g, function(m) { return map[m]; });
  };

  // Filter staff based on search
  const filteredStaff = staffMembers.filter(staff =>
    staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.phone.includes(searchTerm)
  );

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStaff.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getStatusBadge = (isActive) => {
    return isActive ? 
      <span style={styles.statusActive}>Active</span> : 
      <span style={styles.statusInactive}>Inactive</span>;
  };

  const closeModal = () => {
    setShowDetailModal(false);
    setShowEditModal(false);
    setSelectedStaff(null);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Loading staff members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <span style={styles.errorIcon}>⚠️</span>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Staff Management</h1>
      </div>

      {/* Search and Export Section */}
      <div style={styles.controls}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search by name, email or phone..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={styles.searchInput}
          />
        </div>
        <div style={styles.exportButtons}>
          <button 
            style={styles.exportBtnCSV}
            onClick={exportToCSV}
          >
            <span style={styles.btnIcon}>📊</span> CSV
          </button>
          <button 
            style={styles.exportBtnExcel}
            onClick={exportToExcel}
          >
            <span style={styles.btnIcon}>📈</span> Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={{...styles.th, width: '50px'}}>S.No</th>
              <th style={{...styles.th, textAlign: 'left'}}>Name</th>
              <th style={{...styles.th, textAlign: 'left'}}>Email</th>
              <th style={{...styles.th, textAlign: 'left'}}>Phone</th>
              <th style={{...styles.th, textAlign: 'center'}}>Status</th>
              <th style={{...styles.th, textAlign: 'center'}}>Permissions</th>
              <th style={{...styles.th, textAlign: 'center', width: '140px'}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((staff, index) => (
                <tr key={staff._id} style={styles.tableRow}>
                  <td style={{...styles.td, textAlign: 'center'}}>
                    {indexOfFirstItem + index + 1}
                  </td>
                  <td style={{...styles.td, fontWeight: 500}}>
                    <div style={styles.nameCell}>
                      <div style={styles.avatarSmall}>
                        {staff.profileImage ? (
                          <img src={staff.profileImage} alt={staff.name} style={styles.avatarSmallImage} />
                        ) : (
                          <div style={styles.avatarPlaceholderSmall}>
                            {staff.name.split(' ').map(n => n[0]).join('')}
                          </div>
                        )}
                      </div>
                      {staff.name}
                    </div>
                  </td>
                  <td style={styles.td}>{staff.email}</td>
                  <td style={styles.td}>{staff.phone}</td>
                  <td style={{...styles.td, textAlign: 'center'}}>
                    {getStatusBadge(staff.isActive)}
                  </td>
                  <td style={{...styles.td, textAlign: 'center'}}>
                    <div style={styles.permissionsCell}>
                      {staff.permissions.map((perm, idx) => (
                        <span key={idx} style={styles.permissionBadge}>
                          {perm.module} ({perm.paths.length})
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{...styles.td, textAlign: 'center'}}>
                    <div style={styles.actionButtons}>
                      <button 
                        style={styles.btnEdit} 
                        title="Edit"
                        onClick={() => handleEditClick(staff)}
                      >
                        ✏️
                      </button>
                      <button 
                        style={styles.btnView} 
                        title="View Details"
                        onClick={() => fetchStaffById(staff._id)}
                      >
                        👁️
                      </button>
                      <button 
                        style={styles.btnDelete} 
                        title="Delete"
                        onClick={() => handleDeleteStaff(staff._id)}
                        disabled={deleteLoading}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={styles.emptyState}>
                  <div style={styles.emptyContent}>
                    <span style={styles.emptyIcon}>👥</span>
                    <p>No staff members found.</p>
                    {searchTerm && <p style={styles.emptySubtext}>Try adjusting your search</p>}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredStaff.length > 0 && (
        <div style={styles.pagination}>
          <div style={styles.paginationInfo}>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredStaff.length)} of {filteredStaff.length} entries
          </div>
          <div style={styles.paginationControls}>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              style={{...styles.paginationBtn, ...(currentPage === 1 ? styles.paginationBtnDisabled : {})}}
            >
              Previous
            </button>
            <span style={styles.paginationPage}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{...styles.paginationBtn, ...(currentPage === totalPages ? styles.paginationBtnDisabled : {})}}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Staff Detail Modal */}
      {showDetailModal && selectedStaff && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Staff Details</h2>
              <button style={styles.modalCloseBtn} onClick={closeModal}>×</button>
            </div>
            
            {detailLoading ? (
              <div style={styles.modalLoading}>
                <div style={styles.spinnerSmall}></div>
                <p>Loading staff details...</p>
              </div>
            ) : (
              <div style={styles.modalBody}>
                <div style={styles.detailSection}>
                  <div style={styles.detailAvatarLarge}>
                    {selectedStaff.profileImage ? (
                      <img src={selectedStaff.profileImage} alt={selectedStaff.name} style={styles.avatarLargeImage} />
                    ) : (
                      <div style={styles.avatarPlaceholderLarge}>
                        {selectedStaff.name.split(' ').map(n => n[0]).join('')}
                      </div>
                    )}
                  </div>
                  
                  <div style={styles.detailInfo}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Name:</span>
                      <span style={styles.detailValue}>{selectedStaff.name}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Email:</span>
                      <span style={styles.detailValue}>{selectedStaff.email}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Phone:</span>
                      <span style={styles.detailValue}>{selectedStaff.phone}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Status:</span>
                      <span style={styles.detailValue}>
                        {getStatusBadge(selectedStaff.isActive)}
                      </span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Created At:</span>
                      <span style={styles.detailValue}>
                        {new Date(selectedStaff.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Last Login:</span>
                      <span style={styles.detailValue}>
                        {selectedStaff.lastLogin ? new Date(selectedStaff.lastLogin).toLocaleString() : 'Never'}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={styles.detailPermissions}>
                  <h3 style={styles.detailSubtitle}>Permissions</h3>
                  {selectedStaff.permissions && selectedStaff.permissions.length > 0 ? (
                    <div style={styles.permissionsGrid}>
                      {selectedStaff.permissions.map((perm, idx) => (
                        <div key={idx} style={styles.permissionCard}>
                          <div style={styles.permissionModule}>{perm.module}</div>
                          <div style={styles.permissionPaths}>
                            {perm.paths.map((path, pIdx) => (
                              <span key={pIdx} style={styles.permissionPathItem}>
                                <span style={styles.pathName}>{path.path}</span>
                                <span style={{...styles.pathAccess, ...getAccessLevelStyle(path.accessLevel)}}>
                                  {path.accessLevel}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={styles.noPermissions}>No permissions assigned</p>
                  )}
                </div>

                <div style={styles.modalFooter}>
                  <button 
                    style={styles.modalActionBtnEdit}
                    onClick={() => {
                      setShowDetailModal(false);
                      handleEditClick(selectedStaff);
                    }}
                  >
                    ✏️ Edit Staff
                  </button>
                  <button style={styles.modalActionBtn} onClick={closeModal}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && selectedStaff && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={{...styles.modalContent, maxWidth: '800px'}} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit Staff</h2>
              <button style={styles.modalCloseBtn} onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleUpdateStaff} style={styles.modalBody}>
              <div style={styles.editForm}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditChange}
                    style={styles.formInput}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditChange}
                    style={styles.formInput}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleEditChange}
                    style={styles.formInput}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={editFormData.isActive}
                      onChange={handleEditChange}
                      style={styles.formCheckbox}
                    />
                    Active Status
                  </label>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Profile Image</label>
                  <input
                    type="file"
                    name="profileImage"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={styles.formFile}
                  />
                  {editFormData.profileImage && (
                    <p style={styles.fileName}>Selected: {editFormData.profileImage.name}</p>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <div style={styles.permissionsHeader}>
                    <label style={styles.formLabel}>Permissions</label>
                    <button 
                      type="button" 
                      style={styles.addPermissionBtn}
                      onClick={addPermissionModule}
                    >
                      + Add Module
                    </button>
                  </div>

                  {editFormData.permissions.map((perm, moduleIndex) => (
                    <div key={moduleIndex} style={styles.permissionEditCard}>
                      <div style={styles.permissionEditHeader}>
                        <select
                          value={perm.module}
                          onChange={(e) => handlePermissionChange(moduleIndex, 0, 'module', e.target.value)}
                          style={styles.moduleSelect}
                        >
                          <option value="">Select Module</option>
                          {availableModels.map((model) => (
                            <option key={model} value={model}>
                              {model}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          style={styles.removeBtn}
                          onClick={() => removePermissionModule(moduleIndex)}
                        >
                          × Remove Module
                        </button>
                      </div>
                      
                      {perm.paths.map((path, pathIndex) => (
                        <div key={pathIndex} style={styles.pathRow}>
                          <select
                            value={path.path}
                            onChange={(e) => handlePermissionChange(moduleIndex, pathIndex, 'path', e.target.value)}
                            style={styles.pathSelect}
                            disabled={!perm.module}
                          >
                            <option value="">
                              {perm.module ? 'Select Path' : 'Select a module first'}
                            </option>
                            {perm.module && getPathsForModule(perm.module).map((availablePath) => (
                              <option key={availablePath} value={availablePath}>
                                {availablePath}
                              </option>
                            ))}
                          </select>
                          <select
                            value={path.accessLevel}
                            onChange={(e) => handlePermissionChange(moduleIndex, pathIndex, 'accessLevel', e.target.value)}
                            style={styles.accessSelect}
                          >
                            <option value="view">View</option>
                            <option value="edit">Edit</option>
                            <option value="manage">Manage</option>
                          </select>
                          <button
                            type="button"
                            style={styles.removeSmallBtn}
                            onClick={() => removePermissionPath(moduleIndex, pathIndex)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      
                      <button
                        type="button"
                        style={styles.addPathBtn}
                        onClick={() => addPermissionPath(moduleIndex)}
                        disabled={!perm.module}
                      >
                        + Add Path {!perm.module && '(Select module first)'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={styles.modalActionBtn}
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={styles.modalActionBtnSave}
                  disabled={editLoading}
                >
                  {editLoading ? 'Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const getAccessLevelStyle = (accessLevel) => {
  const styles = {
    edit: { background: '#dbeafe', color: '#1e40af' },
    manage: { background: '#fef3c7', color: '#92400e' },
    view: { background: '#d1fae5', color: '#065f46' }
  };
  return styles[accessLevel] || styles.view;
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    background: '#f8fafc',
    minHeight: '100vh',
  },
  
  header: {
    marginBottom: '2rem',
  },
  
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  },
  
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  
  searchWrapper: {
    position: 'relative',
    flex: '1',
    maxWidth: '400px',
  },
  
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    fontSize: '0.9rem',
  },
  
  searchInput: {
    width: '100%',
    padding: '0.65rem 1rem 0.65rem 2.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.95rem',
    background: 'white',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  
  exportButtons: {
    display: 'flex',
    gap: '0.75rem',
  },
  
  exportBtnCSV: {
    padding: '0.65rem 1.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    background: 'white',
    color: '#334155',
    fontWeight: 500,
    fontSize: '0.9rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
  },
  
  exportBtnExcel: {
    padding: '0.65rem 1.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    background: '#217346',
    color: 'white',
    fontWeight: 500,
    fontSize: '0.9rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
  },
  
  btnIcon: {
    fontSize: '1rem',
  },
  
  tableWrapper: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto',
    border: '1px solid #e2e8f0',
  },
  
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.95rem',
  },
  
  tableHeader: {
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
  },
  
  th: {
    padding: '1rem 1.2rem',
    fontWeight: 600,
    color: '#475569',
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'background 0.15s ease',
  },
  
  td: {
    padding: '0.9rem 1.2rem',
    color: '#334155',
    verticalAlign: 'middle',
  },
  
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  
  avatarSmall: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    border: '2px solid #e2e8f0',
  },
  
  avatarSmallImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  
  avatarPlaceholderSmall: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  
  statusActive: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '50px',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: '#dcfce7',
    color: '#166534',
  },
  
  statusInactive: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '50px',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: '#fee2e2',
    color: '#991b1b',
  },
  
  permissionsCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.3rem',
    justifyContent: 'center',
  },
  
  permissionBadge: {
    padding: '0.15rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.7rem',
    fontWeight: 500,
    background: '#eff6ff',
    color: '#1e40af',
    border: '1px solid #bfdbfe',
    whiteSpace: 'nowrap',
  },
  
  actionButtons: {
    display: 'flex',
    gap: '0.4rem',
    justifyContent: 'center',
  },
  
  btnEdit: {
    padding: '0.3rem 0.6rem',
    border: 'none',
    borderRadius: '6px',
    background: '#eff6ff',
    color: '#2563eb',
    cursor: 'pointer',
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
  },
  
  btnView: {
    padding: '0.3rem 0.6rem',
    border: 'none',
    borderRadius: '6px',
    background: '#f0fdf4',
    color: '#16a34a',
    cursor: 'pointer',
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
  },
  
  btnDelete: {
    padding: '0.3rem 0.6rem',
    border: 'none',
    borderRadius: '6px',
    background: '#fef2f2',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
  },
  
  emptyState: {
    padding: '3rem 0',
    textAlign: 'center',
    color: '#94a3b8',
  },
  
  emptyContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
  },
  
  emptyIcon: {
    fontSize: '3rem',
  },
  
  emptySubtext: {
    fontSize: '0.85rem',
    color: '#cbd5e1',
  },
  
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1.5rem',
    padding: '0.5rem 0',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  
  paginationInfo: {
    color: '#64748b',
    fontSize: '0.9rem',
  },
  
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  
  paginationBtn: {
    padding: '0.4rem 1rem',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    background: 'white',
    color: '#334155',
    cursor: 'pointer',
    fontSize: '0.85rem',
    transition: 'all 0.2s ease',
  },
  
  paginationBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  
  paginationPage: {
    color: '#475569',
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '1rem',
  },
  
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1.5rem',
    borderRadius: '12px',
    maxWidth: '500px',
    margin: '2rem auto',
  },
  
  errorIcon: {
    fontSize: '2rem',
  },

  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
    backdropFilter: 'blur(4px)',
  },
  
  modalContent: {
    background: 'white',
    borderRadius: '16px',
    maxWidth: '700px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'slideUp 0.3s ease',
  },
  
  modalHeader: {
    padding: '1.5rem 2rem',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f8fafc',
    borderRadius: '16px 16px 0 0',
  },
  
  modalTitle: {
    margin: 0,
    fontSize: '1.5rem',
    color: '#1e293b',
    fontWeight: 700,
  },
  
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '0 0.5rem',
    transition: 'color 0.2s ease',
  },
  
  modalBody: {
    padding: '2rem',
  },
  
  modalLoading: {
    padding: '3rem',
    textAlign: 'center',
    color: '#64748b',
  },
  
  spinnerSmall: {
    width: '30px',
    height: '30px',
    border: '3px solid #e2e8f0',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
  
  detailSection: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '2rem',
    paddingBottom: '2rem',
    borderBottom: '1px solid #e2e8f0',
    flexWrap: 'wrap',
  },
  
  detailAvatarLarge: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    border: '3px solid #e2e8f0',
  },
  
  avatarLargeImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  
  avatarPlaceholderLarge: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    fontWeight: 700,
  },
  
  detailInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  
  detailLabel: {
    fontWeight: 600,
    color: '#64748b',
    minWidth: '100px',
    fontSize: '0.9rem',
  },
  
  detailValue: {
    color: '#1e293b',
    fontSize: '0.95rem',
  },
  
  detailPermissions: {
    marginTop: '1rem',
  },
  
  detailSubtitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '1rem',
  },
  
  permissionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '1rem',
  },
  
  permissionCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '1rem',
    background: '#f8fafc',
  },
  
  permissionModule: {
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '0.5rem',
    fontSize: '1rem',
  },
  
  permissionPaths: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  
  permissionPathItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.85rem',
    padding: '0.2rem 0',
  },
  
  pathName: {
    color: '#475569',
    textTransform: 'capitalize',
  },
  
  pathAccess: {
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  
  noPermissions: {
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  
  modalFooter: {
    marginTop: '2rem',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  
  modalActionBtn: {
    padding: '0.6rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    background: '#e2e8f0',
    color: '#334155',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '0.9rem',
  },
  
  modalActionBtnEdit: {
    padding: '0.6rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    background: '#3b82f6',
    color: 'white',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '0.9rem',
    marginRight: 'auto',
  },
  
  modalActionBtnSave: {
    padding: '0.6rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    background: '#16a34a',
    color: 'white',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '0.9rem',
  },

  // Edit Form Styles
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  
  formLabel: {
    fontWeight: 600,
    color: '#334155',
    fontSize: '0.9rem',
  },
  
  formInput: {
    padding: '0.65rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  
  formCheckbox: {
    marginRight: '0.5rem',
  },
  
  formFile: {
    padding: '0.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.9rem',
  },
  
  fileName: {
    fontSize: '0.85rem',
    color: '#64748b',
    marginTop: '0.25rem',
  },
  
  permissionsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  
  addPermissionBtn: {
    padding: '0.4rem 1rem',
    border: 'none',
    borderRadius: '6px',
    background: '#3b82f6',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.85rem',
    transition: 'all 0.2s ease',
  },
  
  permissionEditCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
    background: '#f8fafc',
  },
  
  permissionEditHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  
  moduleSelect: {
    flex: 1,
    padding: '0.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '0.9rem',
    minWidth: '150px',
    background: 'white',
    outline: 'none',
  },
  
  removeBtn: {
    padding: '0.3rem 0.8rem',
    border: 'none',
    borderRadius: '4px',
    background: '#fef2f2',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '0.8rem',
    transition: 'all 0.2s ease',
  },
  
  pathRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
  },
  
  pathSelect: {
    flex: 2,
    padding: '0.4rem 0.6rem',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '0.85rem',
    minWidth: '100px',
    background: 'white',
    outline: 'none',
    cursor: 'pointer',
  },
  
  accessSelect: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '0.85rem',
    background: 'white',
    minWidth: '80px',
    outline: 'none',
    cursor: 'pointer',
  },
  
  removeSmallBtn: {
    padding: '0.2rem 0.5rem',
    border: 'none',
    borderRadius: '4px',
    background: '#fef2f2',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '0.8rem',
    transition: 'all 0.2s ease',
  },
  
  addPathBtn: {
    padding: '0.3rem 0.8rem',
    border: '1px dashed #94a3b8',
    borderRadius: '4px',
    background: 'transparent',
    color: '#475569',
    cursor: 'pointer',
    fontSize: '0.8rem',
    transition: 'all 0.2s ease',
    marginTop: '0.25rem',
  },
};

// Add keyframe animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  button:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  input:focus, select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  tr:hover {
    background: #f8fafc;
  }
  
  .modal-close-btn:hover {
    color: #1e293b;
  }
  
  .modal-action-btn:hover {
    background: #cbd5e1;
  }
  
  .modal-action-btn-edit:hover {
    background: #2563eb;
  }
  
  .modal-action-btn-save:hover {
    background: #15803d;
  }
  
  .add-permission-btn:hover {
    background: #2563eb;
  }
  
  .remove-btn:hover {
    background: #fee2e2;
  }
  
  .remove-small-btn:hover {
    background: #fee2e2;
  }
  
  .add-path-btn:hover:not(:disabled) {
    background: #f1f5f9;
  }
  
  .add-path-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f1f5f9;
  }
`;
document.head.appendChild(styleSheet);

export default Staff;